import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "./ui/button.jsx";
import LanguageSwitcher from "./LanguageSwitcher";
import { useI18n } from "../i18n/I18nContext";

const LAST_UPDATED = "March 13, 2026";

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  const { language, t } = useI18n();

  return (
    <div className="min-h-screen bg-[#05060d] text-white">
      <div className="mx-auto max-w-3xl px-6 py-16">
        <div className="mb-10 flex items-center justify-between gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/")}
          className="-ml-2 text-white/60 hover:text-white"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t("Back to site")}
        </Button>
          <LanguageSwitcher />
        </div>

        <h1 className="mb-2 text-3xl font-bold tracking-tight">{t("Privacy & Cookie Policy")}</h1>
        <p className="mb-12 text-sm text-white/50">{t("Last updated:")} {language === "es" ? "13 de marzo de 2026" : LAST_UPDATED}</p>

        {/* ── Privacy Policy ── */}
        <Section title="1. About This Policy">
          <p>
            {t("This Privacy Policy explains how Imago Aerospace (\"Imago\", \"we\", \"our\", or \"us\") collects, uses, and protects information when you visit")}{" "}
            <span className="text-white">imagoaerospace.com</span> {t("or contact us through this website.")}
          </p>
        </Section>

        <Section title="2. Who We Are">
          <p>
            {t("Imago Aerospace is a company that designs and manufactures next-generation VTOL unmanned aerial systems, engineered in Latin America. We are the data controller for information collected through this site.")}
          </p>
          <p className="mt-3">
            {t("To contact us regarding this policy, please use the contact form on our website or email us at")}{" "}
            <a href="mailto:privacy@imagoaerospace.com" className="underline underline-offset-2 hover:text-white transition-colors">
              privacy@imagoaerospace.com
            </a>
            .
          </p>
        </Section>

        <Section title="3. Information We Collect">
          <Subsection title="Information you provide directly">
            <p>{t("When you submit our contact form, we collect:")}</p>
            <ul className="mt-2 list-disc pl-5 space-y-1">
              <li>{t("Full name")}</li>
              <li>{t("Email address")}</li>
              <li>{t("Company name (optional)")}</li>
              <li>{t("Message content (mission description)")}</li>
            </ul>
          </Subsection>
          <Subsection title="Information collected automatically">
            <p>
              {t("When you browse this site, we and our third-party analytics providers automatically collect certain usage information, including pages visited, session duration, browser type, device type, operating system, and approximate geographic location derived from your IP address. IP addresses are anonymized where possible.")}
            </p>
          </Subsection>
        </Section>

        <Section title="4. Legal Basis for Processing">
          <p>{t("We process your personal data on the following grounds:")}</p>
          <ul className="mt-3 list-disc pl-5 space-y-2">
            <li>
              <strong className="text-white">{t("Consent")}</strong> — {t("for analytics and tracking cookies. You may withdraw consent at any time using the cookie settings at the bottom of any page.")}
            </li>
            <li>
              <strong className="text-white">{t("Legitimate interest")}</strong> — {t("to understand how our website is used and improve it, in a way that does not override your privacy rights.")}
            </li>
            <li>
              <strong className="text-white">{t("Pre-contractual steps")}</strong> — {t("to respond to your inquiry when you submit our contact form.")}
            </li>
          </ul>
        </Section>

        <Section title="5. How We Use Your Information">
          <ul className="list-disc pl-5 space-y-2">
            <li>{t("To respond to inquiries and sales requests submitted via the contact form.")}</li>
            <li>{t("To analyze website traffic and usage patterns in order to improve site content and performance.")}</li>
            <li>{t("To detect and prevent bot submissions and abuse of our contact form.")}</li>
          </ul>
          <p className="mt-3">
            {t("We do not sell your personal data to third parties, and we do not use it for advertising profiling.")}
          </p>
        </Section>

        <Section title="6. Third-Party Services">
          <p>{t("We use the following third-party services that may process your data:")}</p>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm text-left border-collapse">
              <thead>
                <tr className="border-b border-white/10 text-white/50 uppercase text-xs tracking-widest">
                  <th className="pb-3 pr-8">{t("Service")}</th>
                  <th className="pb-3 pr-8">{t("Purpose")}</th>
                  <th className="pb-3">{t("Privacy Policy")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                <tr>
                  <td className="py-3 pr-8 text-white">Google Analytics 4</td>
                  <td className="py-3 pr-8 text-white/60">{t("Website traffic analysis")}</td>
                  <td className="py-3">
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer"
                      className="underline underline-offset-2 text-white/60 hover:text-white transition-colors">
                      policies.google.com/privacy
                    </a>
                  </td>
                </tr>
                <tr>
                  <td className="py-3 pr-8 text-white">PostHog</td>
                  <td className="py-3 pr-8 text-white/60">{t("Product analytics")}</td>
                  <td className="py-3">
                    <a href="https://posthog.com/privacy" target="_blank" rel="noreferrer"
                      className="underline underline-offset-2 text-white/60 hover:text-white transition-colors">
                      posthog.com/privacy
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-white/50 text-sm">
            {t("These services are only activated when you consent to analytics cookies.")}
          </p>
        </Section>

        <Section title="7. Data Retention">
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong className="text-white">{t("Contact form submissions")}</strong> — {t("retained for as long as necessary to respond to your inquiry and for up to 2 years thereafter.")}
            </li>
            <li>
              <strong className="text-white">{t("Analytics data")}</strong> — {t("subject to each provider's retention settings (Google Analytics default: 14 months; PostHog: 1 year).")}
            </li>
          </ul>
        </Section>

        <Section title="8. Your Rights">
          <p>{t("Depending on your location, you may have the following rights regarding your personal data:")}</p>
          <ul className="mt-3 list-disc pl-5 space-y-2">
            <li><strong className="text-white">{t("Access")}</strong> — {t("request a copy of the data we hold about you.")}</li>
            <li><strong className="text-white">{t("Rectification")}</strong> — {t("request correction of inaccurate data.")}</li>
            <li><strong className="text-white">{t("Erasure")}</strong> — {t("request deletion of your data.")}</li>
            <li><strong className="text-white">{t("Restriction")}</strong> — {t("request that we limit processing of your data.")}</li>
            <li><strong className="text-white">{t("Portability")}</strong> — {t("receive your data in a structured, machine-readable format.")}</li>
            <li><strong className="text-white">{t("Objection")}</strong> — {t("object to processing based on legitimate interests.")}</li>
            <li><strong className="text-white">{t("Withdraw consent")}</strong> — {t("at any time, for processing based on consent (e.g., analytics cookies).")}</li>
          </ul>
          <p className="mt-4">
            {t("To exercise any of these rights, contact us at")}{" "}
            <a href="mailto:privacy@imagoaerospace.com" className="underline underline-offset-2 hover:text-white transition-colors">
              privacy@imagoaerospace.com
            </a>
            .
          </p>
        </Section>

        <Section title="9. Changes to This Policy">
          <p>
            {t("We may update this policy from time to time. The \"Last updated\" date at the top of this page reflects the most recent revision. We encourage you to review this page periodically.")}
          </p>
        </Section>

        {/* ── Cookie Policy ── */}
        <hr className="my-12 border-white/10" />

        <h2 className="mb-8 text-2xl font-bold tracking-tight">{t("Cookie Policy")}</h2>

        <Section title="What Are Cookies">
          <p>
            {t("Cookies are small text files placed on your device by websites you visit. They are widely used to make websites work, improve performance, and provide information to site owners.")}
          </p>
        </Section>

        <Section title="Cookies We Use">
          <p className="mb-4">
            {t("We do not use cookies that are strictly necessary to operate this website (no login sessions, no shopping cart, etc.). The only cookies placed on your device are analytics cookies, and only when you have given your consent.")}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left border-collapse">
              <thead>
                <tr className="border-b border-white/10 text-white/50 uppercase text-xs tracking-widest">
                  <th className="pb-3 pr-6">Cookie</th>
                  <th className="pb-3 pr-6">{t("Provider")}</th>
                  <th className="pb-3 pr-6">{t("Purpose")}</th>
                  <th className="pb-3">{t("Expiry")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                <tr>
                  <td className="py-3 pr-6 text-white font-mono text-xs">_ga</td>
                  <td className="py-3 pr-6 text-white/60">Google Analytics</td>
                  <td className="py-3 pr-6 text-white/60">{t("Distinguishes unique users")}</td>
                  <td className="py-3 text-white/60">{t("2 years")}</td>
                </tr>
                <tr>
                  <td className="py-3 pr-6 text-white font-mono text-xs">_ga_*</td>
                  <td className="py-3 pr-6 text-white/60">Google Analytics</td>
                  <td className="py-3 pr-6 text-white/60">{t("Persists session state")}</td>
                  <td className="py-3 text-white/60">{t("2 years")}</td>
                </tr>
                <tr>
                  <td className="py-3 pr-6 text-white font-mono text-xs">ph_*</td>
                  <td className="py-3 pr-6 text-white/60">PostHog</td>
                  <td className="py-3 pr-6 text-white/60">{t("Product analytics & session tracking")}</td>
                  <td className="py-3 text-white/60">{t("1 year")}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="Managing Your Cookie Preferences">
          <p className="mb-3">{t("You can change your cookie preferences at any time:")}</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong className="text-white">{t("Cookie banner")}</strong> — {t("reload the page after clearing your browser storage, or use your browser's developer tools to delete the")}{" "}
              <span className="font-mono text-xs text-white">imago_cookie_consent</span> {t("key from localStorage.")}
            </li>
            <li>
              <strong className="text-white">{t("Browser settings")}</strong> — {t("most browsers allow you to refuse or delete cookies. Visit your browser's help section for instructions.")}
            </li>
            <li>
              <strong className="text-white">{t("Google Analytics opt-out")}</strong> — {t("install the")}{" "}
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noreferrer"
                className="underline underline-offset-2 text-white/60 hover:text-white transition-colors">
                {t("Google Analytics Opt-out Browser Add-on")}
              </a>
              .
            </li>
          </ul>
          <p className="mt-4 text-white/50 text-sm">
            {t("Note: declining analytics cookies does not affect your ability to use this website.")}
          </p>
        </Section>

        {/* Back to top */}
        <div className="mt-16 pt-8 border-t border-white/10 flex justify-between items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            className="text-white/60 hover:text-white"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("Back to site")}
          </Button>
          <span className="text-xs text-white/30">© {new Date().getFullYear()} Imago Aerospace</span>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  const { t } = useI18n();
  return (
    <section className="mb-10">
      <h3 className="mb-4 text-lg font-semibold text-white">{t(title)}</h3>
      <div className="text-sm text-white/60 leading-relaxed">{children}</div>
    </section>
  );
}

function Subsection({ title, children }) {
  const { t } = useI18n();
  return (
    <div className="mt-4">
      <h4 className="mb-2 text-sm font-semibold text-white/80">{t(title)}</h4>
      <div className="text-sm text-white/60 leading-relaxed">{children}</div>
    </div>
  );
}
